import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "inline-address-editor__actions"
}
const _hoisted_2 = { class: "inline-address-editor__actions" }
const _hoisted_3 = { class: "inline-address-editor__name" }
const _hoisted_4 = {
  key: 0,
  class: "inline-address-editor__actions"
}

import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AddressSelectorRefactored from "@/components/common/address-selector/AddressSelectorRefactored.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { CommonEntity } from "@/models/interfaces/common";
import { AddressInputComponentOut, AddressInputContainer } from "@/models/interfaces/entity-settings";
import { auxStore } from "@/store/auxillary";
import { chainToRequestContainer, RequestAddressContainer } from "@/composables/useAddressRequest";


export default /*@__PURE__*/_defineComponent({
  __name: 'InlineAddressEditor',
  props: {
    modelValue: {},
    id: {},
    editable: { type: Boolean, default: true },
    showEmptyPlaceholder: { type: Boolean, default: true },
    menuZIndex: { default: 0 },
    alwaysEditing: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "save"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const isEditing = ref(false)
const editedAddress = ref<CommonEntity>({ Id: 0, Name: "" })
const latestChain = ref<AddressInputContainer | null>(null)
const $editorRoot = ref<HTMLElement | null>(null)
const $selector = ref<{ clearAddressModel: () => void } | null>(null)

const structure = ref<AddressInputComponentOut[]>(auxStore().getCachedAddressContainer ?? [])
let baseline = ""
// Гасим собственное эхо update:modelValue, чтобы watcher не ресидил editedAddress
// (и не прерывал ввод/выбор пересозданием по :key). Повторяет guardModelSync селектора.
let echo = false

onMounted(async () => {
	await auxStore().getAddressContainerStructure()
	structure.value = auxStore().getCachedAddressContainer ?? []
	if (props.alwaysEditing) {
		seedEdited()
	}
})

watch(() => props.modelValue, () => {
	if (!props.alwaysEditing || echo) {
		return
	}
	seedEdited()
})

// Значения по уровням из текущего modelValue (по порядку структуры).
const valuesByStructure = (): string[] =>
	structure.value.map(
		s => props.modelValue?.components.find(c => c.level === s.Level)?.value ?? ""
	)

// Текст view-режима: непустые значения, либо плейсхолдеры, если адрес не указан.
const displayText = computed(() => {
	if (!structure.value.length) {
		return ""
	}
	const values = valuesByStructure()
	if (values.some(v => v.trim().length > 0)) {
		return values.filter(v => v.trim().length > 0).join(", ")
	}
	if (!props.showEmptyPlaceholder) {
		return ""
	}
	return structure.value.map(s => s.Placeholder).join(", ")
})

const hasAddress = computed(() => !!props.modelValue && props.modelValue.components.length > 0)

const seedName = (): string => valuesByStructure().join(",")

const seedEdited = () => {
	editedAddress.value = { Id: 0, Name: seedName() }
}

const enterEdit = () => {
	if (!props.editable || isEditing.value) {
		return
	}
	baseline = seedName()
	editedAddress.value = { Id: 0, Name: baseline }
	latestChain.value = null
	isEditing.value = true
}

const blurAddressInput = () => {
	const active = document.activeElement as HTMLElement | null
	if (
		$editorRoot.value &&
		active &&
		$editorRoot.value.contains(active) &&
		typeof active.blur === "function"
	) {
		active.blur()
	}
}

const onChain = (p: { request: AddressInputContainer; correct: boolean }) => {
	latestChain.value = p.request

	if (props.alwaysEditing) {
		echo = true
		emit('update:modelValue', chainToRequestContainer(p.request))
		nextTick(() => {
			echo = false
		})
	}
}

const clearAlways = () => {
	$selector.value?.clearAddressModel?.()
}

const clearAddress = () => {
	editedAddress.value = { Id: 0, Name: "" }

	if (latestChain.value) {
		latestChain.value = {
			Components: latestChain.value.Components.map(c => ({ ...c, Value: "" })),
			ActiveLevel: 0,
		}
	}
}

const cancelEdit = () => {
	editedAddress.value = { Id: 0, Name: baseline }
	latestChain.value = null
	isEditing.value = false
	blurAddressInput()
}

const commit = () => {
	if (!latestChain.value) {
		return
	}
	emit('update:modelValue', chainToRequestContainer(latestChain.value))
	emit('save', latestChain.value)
}

const saveAddress = () => {
	commit()
	isEditing.value = false
	blurAddressInput()
}

const flush = () => {
	if (props.alwaysEditing) {
		emit('update:modelValue', chainToRequestContainer(latestChain.value))
		return
	}
	commit()
	isEditing.value = false
	blurAddressInput()
}

__expose({ flush })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "$editorRoot",
    ref: $editorRoot,
    class: "inline-address-editor"
  }, [
    (_ctx.alwaysEditing)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_openBlock(), _createBlock(AddressSelectorRefactored, {
            ref_key: "$selector",
            ref: $selector,
            id: _ctx.id,
            key: editedAddress.value.Id,
            modelValue: editedAddress.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editedAddress).value = $event)),
            "instant-init": true,
            "menu-z-index": _ctx.menuZIndex,
            onChainSelected: onChain
          }, null, 8, ["id", "modelValue", "menu-z-index"])),
          (hasAddress.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                _createVNode(AppTooltip, { text: "Очистить" }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      class: "inline-address-editor__btn",
                      name: "arrow-left",
                      width: "18px",
                      color: "#979797",
                      "hover-color": "#499259",
                      cursor: "pointer",
                      onClick: clearAlways
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : (isEditing.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_openBlock(), _createBlock(AddressSelectorRefactored, {
              id: _ctx.id,
              key: editedAddress.value.Id,
              modelValue: editedAddress.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editedAddress).value = $event)),
              "instant-init": true,
              "menu-z-index": _ctx.menuZIndex,
              onChainSelected: onChain
            }, null, 8, ["id", "modelValue", "menu-z-index"])),
            _createElementVNode("span", _hoisted_2, [
              _createVNode(AppTooltip, { text: "Сохранить" }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "inline-address-editor__btn",
                    name: "check-circle",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    onClick: saveAddress
                  })
                ]),
                _: 1
              }),
              _createVNode(AppTooltip, { text: "Очистить" }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "inline-address-editor__btn",
                    name: "arrow-left",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    onClick: clearAddress
                  })
                ]),
                _: 1
              }),
              _createVNode(AppTooltip, { text: "Отмена" }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "inline-address-editor__btn",
                    name: "close",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    onClick: cancelEdit
                  })
                ]),
                _: 1
              })
            ])
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(displayText.value), 1),
            (_ctx.editable)
              ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                  _createVNode(AppTooltip, { text: "Редактировать" }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        class: "inline-address-editor__btn",
                        name: "edit-pencil",
                        width: "18px",
                        color: "#979797",
                        "hover-color": "#499259",
                        cursor: "pointer",
                        onClick: enterEdit
                      })
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ], 64))
  ], 512))
}
}

})